<template>
  <div id="app-shell">
    <!-- 全局Toast容器 -->
    <div class="toast-container">
      <transition-group name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-notification"
          :class="toast.type"
        >
          {{ toast.message }}
        </div>
      </transition-group>
    </div>
    
    <nav class="navbar" v-if="!isProductDetailPage" :class="{ 'navbar-hidden': isNavbarHidden }">
      <div class="navbar-container">
        <router-link to="/" class="navbar-logo">dashcom</router-link>
        <div class="navbar-menu">
          <template v-if="!isCustomerLoggedIn && !isSellerLoggedIn">
            <router-link to="/login" class="navbar-link">用户登录</router-link>
            <router-link to="/register" class="navbar-link">注册账号</router-link>
          </template>

          <template v-if="isCustomerLoggedIn">
            <span class="navbar-user">{{ customerUsername || '用户' }}</span>
            <router-link to="/orders" class="navbar-link">我的订单</router-link>
            <router-link to="/favorites" class="navbar-link">我的收藏</router-link>
            <router-link to="/cart" class="navbar-link">购物车</router-link>
            <router-link to="/size-profile" class="navbar-link">尺码管理</router-link>
            <router-link to="/notifications" class="navbar-link notification-link">
              消息
              <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
            </router-link>
            <button @click="customerLogout" class="navbar-btn">退出登录</button>
          </template>

          <template v-if="!isSellerLoggedIn && !isCustomerLoggedIn">
            <router-link to="/seller/login" class="navbar-link">卖家入口</router-link>
          </template>

          <template v-if="isSellerLoggedIn">
            <router-link to="/seller/dashboard" class="navbar-link">卖家后台</router-link>
            <button @click="sellerLogout" class="navbar-btn">退出登录</button>
          </template>
        </div>
      </div>
    </nav>

    <main class="container">
      <router-view v-slot="{ Component, route }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </router-view>
    </main>

    <footer class="footer">
      <p>Version E</p>
      <p>沪ICP备2026003105号-1</p>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isSellerLoggedIn: false,
      isCustomerLoggedIn: false,
      customerUsername: '',
      isNavbarHidden: false,
      lastScrollTop: 0,
      unreadCount: 0,
      sseConnection: null,
      pollingInterval: null,
      toasts: [],
      toastIdCounter: 0
    }
  },
  computed: {
    isProductDetailPage() {
      return this.$route.name === 'productDetail' || this.$route.name === 'productDetailEn'
    }
  },
  created() {
    this.checkLoginStatus()
    window.addEventListener('scroll', this.handleScroll)
    this.connectNotificationSSE()
    this.fetchUnreadCount()
    // 将showToast挂载到window对象供全局使用
    window.showToast = this.showToast.bind(this)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    this.disconnectSSE()
  },
  methods: {
    checkLoginStatus() {
      this.isSellerLoggedIn = !!localStorage.getItem('sellerToken')
      this.isCustomerLoggedIn = !!localStorage.getItem('customerToken')
      this.customerUsername = localStorage.getItem('customerUsername') || ''
    },
    async fetchUnreadCount() {
      if (!this.isCustomerLoggedIn) return
      try {
        const username = localStorage.getItem('customerUsername')
        if (!username) return
        const response = await this.$axios.get('/notifications/unread-count', {
          headers: { 'X-Username': username }
        })
        if (response.data && response.data.success) {
          this.unreadCount = response.data.count
        }
      } catch (e) {
        // Notifications are non-critical.
      }
    },
    connectNotificationSSE() {
      if (!this.isCustomerLoggedIn) return
      const username = localStorage.getItem('customerUsername')
      if (!username) return
      try {
        const baseUrl = this.$axios.defaults.baseURL || '/api'
        this.sseConnection = new EventSource(
          `${baseUrl}/notifications/sse?username=${encodeURIComponent(username)}`
        )
        this.sseConnection.addEventListener('price-change', () => {
          this.unreadCount++
        })
        this.sseConnection.onerror = () => {
          this.disconnectSSE()
          this.startPolling()
        }
      } catch (e) {
        this.startPolling()
      }
    },
    reconnectSSE() {
      this.disconnectSSE()
      this.connectNotificationSSE()
      this.fetchUnreadCount()
    },
    disconnectSSE() {
      if (this.sseConnection) {
        this.sseConnection.close()
        this.sseConnection = null
      }
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    startPolling() {
      if (this.pollingInterval) return
      this.pollingInterval = setInterval(() => {
        this.fetchUnreadCount()
      }, 30000)
    },
    handleScroll() {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.isNavbarHidden = currentScrollTop > this.lastScrollTop && currentScrollTop > 100
      this.lastScrollTop = currentScrollTop
    },
    sellerLogout() {
      localStorage.removeItem('sellerToken')
      localStorage.removeItem('sellerUsername')
      this.isSellerLoggedIn = false
      this.$router.push('/seller/login')
    },
    customerLogout() {
      localStorage.removeItem('customerToken')
      localStorage.removeItem('customerUsername')
      this.isCustomerLoggedIn = false
      this.customerUsername = ''
      this.$router.push('/login')
    },
    // 全局Toast方法
    showToast(message, type = 'info') {
      const toast = {
        id: ++this.toastIdCounter,
        message: message,
        type: type
      }
      this.toasts.push(toast)
      
      // 3秒后自动移除
      setTimeout(() => {
        const index = this.toasts.findIndex(t => t.id === toast.id)
        if (index !== -1) {
          this.toasts.splice(index, 1)
        }
      }, 3000)
    }
  },
  watch: {
    $route() {
      this.checkLoginStatus()
    },
    isCustomerLoggedIn(newVal) {
      if (newVal) {
        this.reconnectSSE()
      } else {
        this.disconnectSSE()
      }
    }
  }
}
</script>

<style>
* {
  box-sizing: border-box;
  font-family: var(--font-sans);
}

#app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  position: fixed;
  top: 14px;
  left: 16px;
  right: 16px;
  z-index: 999;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: var(--backdrop-blur);
  -webkit-backdrop-filter: var(--backdrop-blur);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  box-shadow: var(--shadow-soft);
  transition:
    transform var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

.navbar-hidden {
  transform: translateY(-140%);
}

.navbar-container {
  width: min(100%, var(--content-width));
  margin: 0 auto;
  padding: 0.9rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.navbar-logo {
  text-decoration: none;
  color: var(--color-text);
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.navbar-menu {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.navbar-link,
.navbar-user {
  color: var(--color-text-soft);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.48rem 0.82rem;
  border-radius: 999px;
  transition:
    background-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);
}

.navbar-link:hover {
  background-color: rgba(15, 23, 42, 0.04);
  color: var(--color-text);
}

.navbar-user {
  color: var(--color-text);
}

.navbar-btn,
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.72rem 1.18rem;
  background: #1d1d1f;
  color: #fff;
  border: none;
  border-radius: 999px;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition:
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    background-color var(--duration-fast) var(--ease-standard);
}

.navbar-btn:hover,
.btn:hover {
  transform: translateY(-1px);
  background-color: #2c2c2e;
  box-shadow: var(--shadow-soft);
}

.btn-secondary {
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.btn-secondary:hover {
  background: rgba(15, 23, 42, 0.1);
}

.notification-link {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -2px;
  min-width: 18px;
  padding: 2px 6px;
  border-radius: 999px;
  background: var(--color-accent);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  box-shadow: 0 8px 18px rgba(0, 113, 227, 0.25);
}

.container {
  width: min(100%, var(--content-width));
  flex: 1;
  margin: 0 auto;
  padding: 6.6rem 1.25rem 0;
}

.footer {
  margin-top: auto;
  padding: 2rem 0 2.5rem;
  text-align: center;
  color: var(--color-text-faint);
  border-top: 1px solid rgba(15, 23, 42, 0.04);
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-text);
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.82rem 0.95rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.92);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: rgba(0, 113, 227, 0.45);
  box-shadow: var(--shadow-focus);
}

.card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 1.2rem;
  margin-bottom: 1rem;
  background: var(--color-surface);
  backdrop-filter: var(--backdrop-blur);
  -webkit-backdrop-filter: var(--backdrop-blur);
  box-shadow: var(--shadow-soft);
}

.alert {
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: var(--radius-sm);
}

.alert-success {
  background: rgba(31, 157, 85, 0.1);
  color: #1c6b3f;
  border: 1px solid rgba(31, 157, 85, 0.18);
}

.alert-danger {
  background: rgba(192, 57, 43, 0.09);
  color: #9d3024;
  border: 1px solid rgba(192, 57, 43, 0.14);
}

.alert-info {
  background: rgba(0, 113, 227, 0.08);
  color: var(--color-accent-strong);
  border: 1px solid rgba(0, 113, 227, 0.12);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(8px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  width: min(92vw, 520px);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-medium);
  overflow: hidden;
  animation: slideUp 0.28s var(--ease-standard);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header,
.modal-footer {
  padding: 1.2rem 1.4rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--color-border);
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  border-top: 1px solid var(--color-border);
  background: rgba(15, 23, 42, 0.03);
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-faint);
  cursor: pointer;
  transition:
    background-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);
}

.close-btn:hover {
  background: rgba(15, 23, 42, 0.06);
  color: var(--color-text);
}

/* Toast样式 - 配合网站整体风格 */
.toast-container {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast-notification {
  padding: 14px 20px;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--color-text);
  min-width: 220px;
  max-width: 380px;
  text-align: left;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: var(--shadow-soft);
  display: flex;
  align-items: center;
  gap: 10px;
}

.toast-notification::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--color-accent);
}

.toast-notification.success {
  border-color: rgba(31, 157, 85, 0.25);
}

.toast-notification.success::before {
  background: #16a34a;
}

.toast-notification.error {
  border-color: rgba(192, 57, 43, 0.25);
}

.toast-notification.error::before {
  background: #dc2626;
}

.toast-notification.warning {
  border-color: rgba(202, 138, 4, 0.25);
}

.toast-notification.warning::before {
  background: #ca8a04;
}

/* Toast动画 */
.toast-enter-active,
.toast-leave-active {
  transition: all var(--duration-base) var(--ease-standard);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform var(--duration-base) var(--ease-standard);
}

@media (max-width: 900px) {
  .navbar {
    top: 10px;
    left: 10px;
    right: 10px;
    border-radius: 24px;
  }

  .navbar-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .navbar-menu {
    width: 100%;
    justify-content: flex-start;
  }

  .container {
    padding-top: 7.6rem;
  }
  
  .toast-container {
    right: 10px;
    left: 10px;
  }
  
  .toast-notification {
    min-width: auto;
  }
}
</style>